import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import useGetDeliverableMediaRights from "@entities/deliverableConfigurator/lib/hooks/useGetDeliverableMediaRights";
import useSyncMediaRights from "@entities/deliverableConfigurator/lib/hooks/useSyncMediaRights";
import { ToastService } from "@shared/lib/services";
import { Button } from "@shared/ui/Button";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { Flex, Input, List, Popconfirm, Spin, Typography, message } from "antd";
import { useEffect, useState } from "react";

export default function MediaRightsConfigurator() {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [formValue, setFormValue] = useState<{
    name: string;
    description: string;
  }>({
    name: "",
    description: "",
  });

  const {
    deliverableMediaRightsData,
    isDeliverableMediaRightsFetching,
    deliverableMediaRightsError,
  } = useGetDeliverableMediaRights();

  const {
    syncMediaRights,
    isSyncMediaRightsLoading,
    isSyncMediaRightsSuccess,
    syncMediaRightsError,
  } = useSyncMediaRights();

  const mediaRights = deliverableMediaRightsData?.data?.types || [];

  // Create a combined dataSource that includes the create item when needed
  const listDataSource = isCreating
    ? [{ __isCreating: true, value: "" }, ...mediaRights]
    : mediaRights;

  useEffect(() => {
    if (deliverableMediaRightsError) {
      ToastService.error(deliverableMediaRightsError?.message);
    }
  }, [deliverableMediaRightsError]);

  useEffect(() => {
    if (isSyncMediaRightsSuccess) {
      ToastService.success(
        editingIndex !== null
          ? "Media right updated successfully"
          : isCreating
          ? "Media right created successfully"
          : "Media right updated successfully"
      );
      setEditingIndex(null);
      setIsCreating(false);
      setFormValue({
        name: "",
        description: "",
      });
    }
  }, [isSyncMediaRightsSuccess]);

  useEffect(() => {
    if (syncMediaRightsError) {
      ToastService.error(syncMediaRightsError?.message);
    }
  }, [syncMediaRightsError]);

  const handleCreate = () => {
    setEditingIndex(null);
    setIsCreating(true);
    setFormValue({
      name: "",
      description: "",
    });
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setIsCreating(false);
    setFormValue(mediaRights[index]);
  };

  const handleDelete = (index: number) => {
    const updatedRights = mediaRights.filter((_, i: number) => i !== index);
    syncMediaRights(updatedRights);
  };

  const handleSubmit = () => {
    if (!formValue?.name?.trim()) {
      message.error("Media right cannot be empty");
      return;
    }

    let updatedRights: {
      name: string;
      description: string;
    }[];
    if (editingIndex !== null) {
      // Edit existing
      updatedRights = [...mediaRights];
      updatedRights[editingIndex] = {
        name: formValue?.name?.trim(),
        description: formValue?.description,
      };
    } else if (isCreating) {
      // Create new
      updatedRights = [
        ...mediaRights,
        {
          name: formValue?.name?.trim(),
          description: formValue?.description,
        },
      ];
    } else {
      return;
    }

    syncMediaRights(updatedRights);
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setIsCreating(false);
    setFormValue({
      name: "",
      description: "",
    });
  };

  return (
    <Spin
      spinning={isDeliverableMediaRightsFetching || isSyncMediaRightsLoading}
    >
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Typography.Title level={5}>Media Rights</Typography.Title>
        <Button
          icon={<PlusOutlined />}
          variant="outlined"
          onClick={handleCreate}
        >
          Add New Media Right
        </Button>
      </Flex>

      <List
        dataSource={listDataSource}
        renderItem={(item, index) => {
          // Handle create item
          if (item && typeof item === "object" && "__isCreating" in item) {
            return (
              <List.Item key="create-item">
                <Flex vertical gap={10} style={{ width: "100%" }}>
                  <Input
                    value={formValue?.name}
                    onChange={(e) =>
                      setFormValue((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    placeholder="Enter media right"
                    // onPressEnter={handleSubmit}
                    autoFocus
                    style={{ width: "100%" }}
                  />
                  <Input.TextArea
                    name="description"
                    value={formValue?.description}
                    onChange={(e) =>
                      setFormValue((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Enter description"
                    autoFocus
                    style={{ width: "100%" }}
                  />
                  <Flex gap={10} justify="flex-end">
                    <Button
                      key="save"
                      icon={<CheckOutlined />}
                      variant="text"
                      onClick={handleSubmit}
                      loading={isSyncMediaRightsLoading}
                    >
                      Save
                    </Button>
                    <Button
                      key="cancel"
                      icon={<CloseOutlined />}
                      variant="text"
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                  </Flex>
                </Flex>
              </List.Item>
            );
          }

          // Adjust index for actual media rights (subtract 1 if creating)
          const actualIndex = isCreating ? index - 1 : index;

          return (
            <List.Item
              actions={
                editingIndex === actualIndex
                  ? []
                  : [
                      <Button
                        key="edit"
                        icon={<EditOutlined />}
                        variant="text"
                        onClick={() => handleEdit(actualIndex)}
                      >
                        Edit
                      </Button>,
                      <Popconfirm
                        key="delete"
                        title="Delete Media Right"
                        description="Are you sure you want to delete this media right?"
                        onConfirm={() => handleDelete(actualIndex)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button icon={<DeleteOutlined />} variant="text" danger>
                          Delete
                        </Button>
                      </Popconfirm>,
                    ]
              }
            >
              {editingIndex === actualIndex ? (
                <Flex vertical gap={10} style={{ width: "100%" }}>
                  <Input
                    value={formValue?.name}
                    onChange={(e) =>
                      setFormValue((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    placeholder="Enter media right"
                    onPressEnter={handleSubmit}
                    autoFocus
                    style={{ width: "100%" }}
                  />
                  <Input.TextArea
                    name="description"
                    value={formValue?.description}
                    onChange={(e) =>
                      setFormValue((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Enter description"
                    autoFocus
                    style={{ width: "100%" }}
                  />
                  <Flex gap={10} justify="flex-end">
                    <Button
                      key="save"
                      icon={<CheckOutlined />}
                      variant="text"
                      onClick={handleSubmit}
                      loading={isSyncMediaRightsLoading}
                    >
                      Save
                    </Button>
                    <Button
                      key="cancel"
                      icon={<CloseOutlined />}
                      variant="text"
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                  </Flex>
                </Flex>
              ) : (
                <Flex vertical>
                  <Typography.Text>{`Name: ${item?.name}`}</Typography.Text>
                  <TextWithTooltip text={`Description: ${item?.description}`} />
                </Flex>
              )}
            </List.Item>
          );
        }}
        locale={{
          emptyText: !isCreating ? (
            <Typography.Text type="secondary">
              No media rights configured
            </Typography.Text>
          ) : null,
        }}
        pagination={false}
      />
    </Spin>
  );
}
